import {
    formatBidItemChangePoint,
    isValidBidItemChangePoint,
    runBidItemChangePointChecks,
} from "components/bid/bidItemChanges/form";
import { resourceEnum } from "components/expenses/constants";
import BaseBidForm from "components/shared/baseBidForm";
import {
    distributionBidInProcessModal,
    isDistributionBidInProcessError,
} from "components/shared/distributionBidInProcessModal";
import InlineBtn from "components/shared/inlineBtn";
import Loader from "components/shared/loader";
import TypeOptions from "components/shared/typeOptions";
import { ProvideTabIndex } from "contexts/tabIndex";
import {
    checkActiveBids,
    checkCompoundBids,
    comparePointsWithSegments,
    parallelItemChangeModalTitle,
} from "helpers/bid";
import { clearDates, withoutStartPoint } from "helpers/bidpoint";
import { calculateBidAverageRate, calculateBidFinalPrice, getVatValueByNdsTypeId } from "helpers/bidRates";
import { dateTimeToView } from "helpers/date";
import {
    bidPointTypesEnum,
    bidStatuses,
    bidStatusesEnum,
    bidTypesEnum,
    catalogEntityOptionFlags,
    catalogEntityOptionFlagsEnum,
    entityTypesEnum,
    featureModuleTypesEnum,
    forwardingOrderChangeBehavioursEnum,
    mapObjectTypesEnum,
    organizationFlags,
    organizationFlagsEnum,
    organizationRegistrationStatusesEnum,
    parallelPointBehaviorsEnum,
} from "helpers/enums";
import {
    defaultExtendedPropValue,
    formatExtendedProperties,
    formatExtendedPropertiesForSave,
    formatInitial,
    logInvalidKeys,
    serializeReq,
    validateForm,
} from "helpers/form";
import { HiddenDataWrapper } from "helpers/hiddenData";
import { cargo } from "helpers/models";
import { paths } from "helpers/paths";
import { canManageOrganization, flagVal, ymEvent } from "helpers/system";
import { UmamiManager } from "lib/umami";
import debounce from "lodash/debounce";
import moment from "moment";
import policy from "policies";
import React from "react";
import { alert } from "services/alert";
import SignalRConnection from "services/signalRConnection";
import { getBidItemsChanges } from "sources/bidItemChanges";
import {
    applyCloseBid,
    applyOpenBid,
    checkForwardingOrderCarHasChanged,
    checkItemsBeforeParallelPoint,
    closeBidsList,
    deleteBid,
    getAverageRateByDestinationForBid,
    getCargoBidForEdit,
    getItemChangePoints,
    getItemIds,
    getTruckingBidForEdit,
    getUnstableZones,
    setStatus,
    startBid,
} from "sources/books/bids";
import { getDocuments, getItem, giveAccessToBids } from "sources/books/cargoOwners";
import { getCouplingByBid } from "sources/books/couplings";
import {
    getDistributionBid,
    getForBid,
    releaseInProcessStatus,
    setInProcessStatus,
} from "sources/books/distributionBids";
import { getBidItem } from "sources/books/emergencyArea";
import { getMain as getMainLegalPerson } from "sources/books/legalPersons";
import { checkDocuments } from "sources/books/organizationDocuments";
import { getPlannedRefuelings } from "sources/books/refueling";
import { getBidItem as getRouteControlBidPoints } from "sources/books/routeControlPoints";
import { getSettings } from "sources/books/tasks";
import { getAvailabilityStatus as getTrailerAvailabilityStatus } from "sources/books/trailers";
import { getAvailabilityStatus } from "sources/books/trucks";
import { getForwardingOrder } from "sources/forwarding";
import { applyBid } from "sources/forwardingBids";
import { calculateRouteDistance, getLastCarLocationAddress, getRouteByBidId } from "sources/map";
import { showDefaultRouteOptions } from "sources/profile/organization";
import { applyOnboarding } from "sources/profile/user";
import { emptyMileageBidConfig, emptyMileageBidDefaultValues } from "./config";
import BooksFields from "./partials/booksSection";
import Cargo from "./partials/cargoElem";
import DriverPayment from "./partials/driverPaymentSection";
import Expense from "./partials/expenseElem";
import ExpressBid from "./partials/expressBid";
import GroupElems from "./partials/groupElemsSection";
import LogistTaskSettings from "./partials/logistTaskSettings";
import MainData from "./partials/mainDataSection";
import Payment from "./partials/paymentSection";
import PlannedItemChangePoints from "./partials/plannedItemChangePoints";
import Point from "./partials/pointElem";
import Route from "./partials/routeSection";

const CUSTOM_FIELDS_ENABLED_BY_ORG = {
    localhost: [13352],
    "test.cargorun.ru": [10765825],
    "test2.cargorun.ru": [10765825],
    "app.cargorun.ru": [124230882],
    "app.cargo.run": [124230882],
    "prod.integration.cargorun.ru": [647064],
};

const autoSetMainContractFlagValue = flagVal(organizationFlags, organizationFlagsEnum.AutoSetMainContract);
const trackEmptyMileageBidTypesFlagValue = flagVal(organizationFlags, organizationFlagsEnum.TrackEmptyMileageBidTypes);
const catalogOptionHiddenFlagValue = flagVal(catalogEntityOptionFlags, catalogEntityOptionFlagsEnum.Invisible);
const catalogOptionShowInEmptyMileageBidsFlagValue = flagVal(
    catalogEntityOptionFlags,
    catalogEntityOptionFlagsEnum.ShowInEmptyMileageBids,
);

export default class FormBid extends BaseBidForm {
    state = {
        isLoading: true,
        isValid: true,
        pointsNotComparedToSegments: false,
        counterpartiesData: {},
        unstableZones: [],
        needRemoveInProcess: true,
        lockedDistributionBidId: null,
        itemIds: { carIds: [], driverIds: [], trailerIds: [] },
        isFirstBid: false,
        averageRateByDestination: null,
        averageRateDate: null,
        bidAverageRate: null,
        bidFinalPrice: null,
        routeDistanceMeters: null,
    };

    lastCarLocationAddressRequestSequence = 0;
    lockedDistributionBidId = null;
    needRemoveInProcess = true;
    pendingDistributionBidLockId = null;

    constructor(props) {
        super(props);
        const { profile, roles, action, isPublic, config, organization } = props;
        this.bidPolicy = policy("bid", {
            user: { profile, roles, organization },
            record: {},
            options: { action, isPublic },
        });
        this.temperatureRegimeEnabled = profile.availableFeatureModules.includes(
            featureModuleTypesEnum.TemperatureRegime,
        );
        this.autoSetMainContractEnabled =
            (autoSetMainContractFlagValue & this.props.organization.intFlags) === autoSetMainContractFlagValue;
        this.trackEmptyMileageBidTypesEnabled =
            (trackEmptyMileageBidTypesFlagValue & this.props.organization.intFlags) ===
            trackEmptyMileageBidTypesFlagValue;
        this.checkVersion = debounce(this.checkVersion, 2500);
        this.calculateAverageRate = debounce(this._calculateAverageRate, 250);
        this.rateCalculationRequestSequence = 0;
        this.initialRateValues = null;
        this.customFieldsEnabled = CUSTOM_FIELDS_ENABLED_BY_ORG[window.location.hostname]?.includes(organization.id);

        if (!this.temperatureRegimeEnabled) {
            delete config.temperatureRegime;
        }
    }

    componentDidMount() {
        this.mounted = true;
        this.initForm();
        this.props.clearData();
        checkDocuments().then((res) => this.setState({ documentsExists: res }));
        const { roles, profile, action } = this.props;
        if (policy("user", { user: { roles, profile } }).hasWorkRestInfoAccess()) {
            this.props.getWorkRestInfo();
        }
        if (this.getDistributionBidId()) {
            window.addEventListener("beforeunload", this.removeInProcessStatusOnPageExit);
            window.addEventListener("pagehide", this.removeInProcessStatusOnPageExit);
        }

        const isSelfRegistered = !!this.props.organization?.registrationStatus;
        const isFirstBid =
            !this.props.isOnboardingSuppressed &&
            isSelfRegistered &&
            !this.props.onboardingOptions?.isCreateBidButtonFinished;

        if (isFirstBid) {
            this.setState({ isFirstBid: true });
            if (action === "edit") {
                ymEvent("AfterRegistrationDraftCreated");
            }
        }
    }

    componentWillUnmount() {
        this.mounted = false;
        this.connection?.disconnect();
        if (this.getDistributionBidId()) {
            this.removeInProcessStatusForDistributionBid();
            window.removeEventListener("beforeunload", this.removeInProcessStatusOnPageExit);
            window.removeEventListener("pagehide", this.removeInProcessStatusOnPageExit);
        }
    }

    componentDidUpdate(prevProps) {
        if (
            !prevProps.workRestInfo?.useLastCarLocationForEmptyMileageBid &&
            this.props.workRestInfo?.useLastCarLocationForEmptyMileageBid
        ) {
            this.fillFirstEmptyMileagePointAddressFromCarLocation();
        }
    }

    UNSAFE_componentWillUpdate(nextProps, nextState) {
        const { profile, roles, action, isPublic, organization } = nextProps;
        this.bidPolicy = policy("bid", {
            user: { profile, roles, organization },
            record: nextState,
            options: { action, isPublic },
        });
    }

    setNeedRemoveInProcess = (needRemoveInProcess) => {
        this.needRemoveInProcess = needRemoveInProcess;
        if (this.mounted) {
            this.setState({ needRemoveInProcess });
        }
    };

    clearDistributionBidLockState = () => {
        this.lockedDistributionBidId = null;
        this.pendingDistributionBidLockId = null;
        if (this.mounted) {
            this.setState({ lockedDistributionBidId: null });
        }
    };

    getDistributionBidLockIdForCleanup = () => {
        const lockedDistributionBidId = this.lockedDistributionBidId || this.state.lockedDistributionBidId;

        if (lockedDistributionBidId) {
            return lockedDistributionBidId;
        }

        // Backend lets admins release locks owned by other users, so a pending lock is unsafe until ownership is confirmed.
        if (this.props.roles?.isTruckingOrganizationAdmin) {
            return null;
        }

        return this.pendingDistributionBidLockId;
    };

    removeInProcessStatusOnPageExit = () => {
        this.removeInProcessStatusForDistributionBid({ keepalive: true, showModal: false });
    };

    removeInProcessStatusForDistributionBid = ({ keepalive = false, showModal = true } = {}) => {
        const lockedDistributionBidId = this.getDistributionBidLockIdForCleanup();

        if (this.needRemoveInProcess && lockedDistributionBidId) {
            if (showModal) {
                this.props.modal({
                    name: "default",
                    withoutHeader: true,
                    CustomBody: (modalProps) => (
                        <div>
                            <div className="text-center pb-4">
                                Данные заказа не сохранены, отправлен запрос на сброс признака &#34;Заполняется&#34;
                            </div>
                            <div className="d-flex justify-content-center">
                                <div className="btn btn-sm btn-brand" onClick={() => modalProps.back()}>
                                    Принять
                                </div>
                            </div>
                        </div>
                    ),
                });
            }
            releaseInProcessStatus(lockedDistributionBidId, { keepalive }).then((res) => {
                if (res) {
                    this.clearDistributionBidLockState();
                    this.setNeedRemoveInProcess(false);
                }
            });
        }
    };

    getDistributionBidId = () => this.props.distributionBidId || this.props.cargorunId;

    closeDistributionBidForm = () => {
        const distributionBidId = this.getDistributionBidId();

        if (distributionBidId) {
            this.props.history.push(paths.distributionBidShow(distributionBidId));
        } else {
            this.props.back();
        }
    };

    showDistributionBidInProcessModal = ({ message, userName } = {}) => {
        this.props.modal(
            distributionBidInProcessModal({
                buttonText: "Закрыть форму",
                message,
                onCloseAction: this.closeDistributionBidForm,
                userName,
            }),
        );
    };

    updateCarAvailability = (value) => {
        this.setState({ carStatus: null });
        if (this.bidPolicy.needUpdateCarAvailability({ value })) {
            getAvailabilityStatus(value).then((carStatus) => this.setState({ carStatus }));
        }
    };

    updateTrailerAvailability = (value) => {
        this.setState({ trailerStatus: null });
        if (this.bidPolicy.needUpdateTrailerAvailability({ value })) {
            getTrailerAvailabilityStatus(value).then((trailerStatus) => this.setState({ trailerStatus }));
        }
    };

    getEmptyMileageExtendedPropertiesConfig = (extendedProperties) => {
        const propertiesConfig = extendedProperties?.config || {};
        return Object.entries(propertiesConfig).reduce((acc, [key, property]) => {
            if (this.customFieldsEnabled || property.showInEmptyMileageBids) {
                acc[key] = property;
            }
            return acc;
        }, {});
    };

    getEmptyMileageBidConfig = () => {
        const { config } = this.props;
        const extendedPropertiesConfig = this.getEmptyMileageExtendedPropertiesConfig(config.extendedProperties);
        const bidPointExtendedPropertiesConfig = this.getEmptyMileageExtendedPropertiesConfig(
            config.bidPoints.config.extendedProperties,
        );
        const cargoExtendedPropertiesConfig = this.getEmptyMileageExtendedPropertiesConfig(
            config.cargos.config.extendedProperties,
        );

        return {
            ...emptyMileageBidConfig,
            extendedProperties: {
                config: extendedPropertiesConfig,
            },
            bidPoints: {
                ...emptyMileageBidConfig.bidPoints,
                config: {
                    ...emptyMileageBidConfig.bidPoints.config,
                    extendedProperties: {
                        config: bidPointExtendedPropertiesConfig,
                    },
                },
            },
            cargos: {
                config: {
                    extendedProperties: {
                        config: cargoExtendedPropertiesConfig,
                    },
                },
            },
        };
    };

    isValidForm = (state) => {
        const config = { ...(state.isEmptyMileageBid ? this.getEmptyMileageBidConfig() : this.props.config) };
        return validateForm(config, serializeReq(state));
    };

    setValue = (changedData) => {
        const { name, value, option } = changedData;
        const nextState = { ...this.state, [name]: value };
        const isValid = this.isValidForm(nextState);
        this.setState({ [name]: value, ...(isValid ? { isValid: true } : {}) });

        if (name === "cargoOwnerDictionaryItemId") {
            const { legalPersonId } = this.state;
            this.updateCounterpartiesData(value, option);
            this.autoSelectCounterpartyContract(value, legalPersonId);
            this.updateCargoOwnerShareAccess(option);
        }
        if (name === "legalPersonId") {
            const { ndsTypeId, paymentTypeId } = option || {};
            const newState = { contractId: null, legalPersonBankAccountId: null };
            if (ndsTypeId) {
                newState.ndsTypeId = ndsTypeId;
            }
            if (paymentTypeId) {
                newState.paymentTypeId = paymentTypeId;
            }
            this.setState(newState);
        }
        if (name === "coupling") {
            this.setState({
                couplingDates: { startedAt: changedData.option?.startedAt, endedAt: changedData.option?.endedAt },
            });
        }
        if (name === "contractId") {
            if (!value) {
                return;
            }
            const { paymentPeriod, paymentPeriodType, paymentTypeId, invoiceTriggerType } = option || {};
            this.setState({ paymentPeriodInDays: paymentPeriod, paymentPeriodType, paymentTypeId, invoiceTriggerType });
        }
        if (name === "isEmptyMileageBid" && !value) {
            this.clearPlanLeaveDates();
        }
        if (this.bidPolicy.needPreloadDataByCoupling({ changedData })) {
            this.loadDataByCoupling(value, option);
        }
        if (this.bidPolicy.needPreloadDataByCar({ changedData })) {
            this.loadDataByCar(value);
        }
        if (this.bidPolicy.needPreloadDataByDriver({ changedData })) {
            this.loadDataByDriver(value);
        }
        if (this.bidPolicy.needPreloadDataByCargoOwner({ changedData })) {
            this.loadDataByCargoOwner(value);
        }
        if (
            [
                "bidPoints",
                "trailerOption",
                "isEmptyMileageBid",
                "price",
                "priceWithoutVatOnTop",
                "isVatTop",
                "ndsTypeId",
            ].includes(name)
        ) {
            this.calculateAverageRate();
        }
        if ((name === "isEmptyMileageBid" && value) || (name === "carOption" && value)) {
            this.fillFirstEmptyMileagePointAddressFromCarLocation(nextState);
        }
    };

    setItemChangePointsValue = (changedData) => {
        this.setValue(changedData);
        if (!this.bidPolicy.isNewForm()) {
            this.setState({ itemChangePointsChanged: true });
        }
    };

    _calculateAverageRate = () => {
        const { bidPoints, isEmptyMileageBid, trailerOption } = this.state;
        const routeBidPoints = withoutStartPoint(bidPoints || []);

        if (isEmptyMileageBid) {
            this.rateCalculationRequestSequence += 1;
            this.setState({
                averageRateByDestination: null,
                averageRateDate: null,
                bidAverageRate: null,
                bidFinalPrice: null,
                routeDistanceMeters: null,
            });
            return;
        }

        if (!routeBidPoints || routeBidPoints.length < 2) {
            this.rateCalculationRequestSequence += 1;
            this.setState({
                averageRateByDestination: null,
                averageRateDate: null,
                bidAverageRate: null,
                bidFinalPrice: null,
                routeDistanceMeters: null,
            });
            return;
        }

        const firstPoint = routeBidPoints[0];
        const lastPoint = routeBidPoints[routeBidPoints.length - 1];
        const routePoints = [];
        const hasAllRouteCoordinates = routeBidPoints.every((point) => {
            const coordinates = point?.geozone?.location?.coordinates;

            if (!coordinates || coordinates.length < 2) {
                return false;
            }

            routePoints.push([coordinates[0], coordinates[1]]);
            return true;
        });
        const hasFirstAndLastCoordinates =
            !!firstPoint?.geozone?.location?.coordinates &&
            !!lastPoint?.geozone?.location?.coordinates &&
            firstPoint.geozone.location.coordinates.length >= 2 &&
            lastPoint.geozone.location.coordinates.length >= 2;
        const planEnterDateFormatted = firstPoint?.planEnterDate
            ? moment(firstPoint.planEnterDate).format("YYYY-MM-DDTHH:mm")
            : null;

        const averageRatePromise =
            hasFirstAndLastCoordinates && planEnterDateFormatted
                ? (() => {
                      const params = {
                          planEnterDate: planEnterDateFormatted,
                          from: [
                              firstPoint.geozone.location.coordinates[0],
                              firstPoint.geozone.location.coordinates[1],
                          ],
                          to: [lastPoint.geozone.location.coordinates[0], lastPoint.geozone.location.coordinates[1]],
                      };

                      if (trailerOption?.trailerId) {
                          params.trailerId = parseInt(trailerOption.trailerId, 10);
                      }

                      return getAverageRateByDestinationForBid(params).catch(() => null);
                  })()
                : Promise.resolve(null);
        const routeDistancePromise = hasAllRouteCoordinates
            ? calculateRouteDistance({ points: routePoints }).catch(() => null)
            : Promise.resolve(null);
        const requestSequence = ++this.rateCalculationRequestSequence;

        Promise.all([averageRatePromise, routeDistancePromise]).then(
            ([averageRateByDestination, routeDistanceMeters]) => {
                if (!this.mounted) {
                    return;
                }

                if (this.rateCalculationRequestSequence !== requestSequence) {
                    return;
                }

                const bidFinalPrice = this.getBidFinalPriceForRateCalculation();
                const bidAverageRate = calculateBidAverageRate({ finalPrice: bidFinalPrice, routeDistanceMeters });

                this.setState({
                    averageRateByDestination: averageRateByDestination ?? null,
                    averageRateDate: averageRateByDestination != null ? planEnterDateFormatted : null,
                    routeDistanceMeters: routeDistanceMeters ?? null,
                    bidFinalPrice,
                    bidAverageRate,
                });
            },
        );
    };

    getBidFinalPriceForRateCalculation = () => {
        const { price, priceWithoutVatOnTop, isVatTop, ndsTypeId } = this.state;
        const vatValue = getVatValueByNdsTypeId(this.props.enums.nDSType, ndsTypeId);
        const effectivePriceWithoutVatOnTop =
            isVatTop && [null, undefined, ""].includes(priceWithoutVatOnTop) ? price : priceWithoutVatOnTop;
        const useStoredPrice =
            this.props.action === "edit" &&
            !!this.initialRateValues &&
            isVatTop &&
            this.initialRateValues.isVatTop === isVatTop &&
            this.initialRateValues.ndsTypeId === ndsTypeId &&
            this.initialRateValues.priceWithoutVatOnTop === priceWithoutVatOnTop;

        return calculateBidFinalPrice({
            price,
            priceWithoutVatOnTop: effectivePriceWithoutVatOnTop,
            isVatTop,
            vatValue,
            useStoredPrice,
        });
    };

    updateCargoOwnerShareAccess = (option) => {
        if (!option) {
            return;
        }
        getItem({ id: option.id }).then((counterparty) => {
            this.setValue({
                name: "cargoOwnerShareAccess",
                value: {
                    kppDisabled: !!counterparty?.kpp,
                    hasAccessToBids: !!counterparty?.hasAccess,
                    kpp: counterparty?.kpp || "",
                    name: counterparty?.name || "",
                    inn: counterparty?.inn || "",
                    contactPerson: {
                        firstName: counterparty.contactPerson?.firstName || "",
                        lastName: counterparty.contactPerson?.lastName || "",
                        patronymic: counterparty.contactPerson?.patronymic || "",
                        phoneNumber: counterparty.contactPerson?.phoneNumber || "",
                        email: counterparty.contactPerson?.email || "",
                    },
                },
            });
            if (option?.hasAccessToBids) {
                this.setState({ shareAccessEnabled: false });
            }
        });
    };

    clearPlanLeaveDates = () => {
        const { bidPoints } = this.state;
        bidPoints.forEach((point) => {
            if ([bidPointTypesEnum.LoadPoint, bidPointTypesEnum.UnloadPoint].includes(point.type)) {
                point.planLeaveDate = null;
                point.planLeaveDateOffset = null;
            }
        });
        this.setState({ bidPoints });
    };

    getSelectedCarId = (state = this.state) => state.carOption?.carId || state.carOption?.id;

    getFirstBidPointIndex = (bidPoints = []) =>
        (bidPoints || []).findIndex(({ type }) => type !== bidPointTypesEnum.StartPoint);

    fillFirstEmptyMileagePointAddressFromCarLocation = (state = this.state) => {
        if (
            this.props.action !== "new" ||
            !state.isEmptyMileageBid ||
            !this.props.workRestInfo?.useLastCarLocationForEmptyMileageBid
        ) {
            return;
        }

        const carId = this.getSelectedCarId(state);
        const firstBidPointIndex = this.getFirstBidPointIndex(state.bidPoints);
        const firstBidPoint = state.bidPoints?.[firstBidPointIndex];
        if (!carId || firstBidPointIndex < 0 || firstBidPoint?.geozone?.address) {
            return;
        }

        const requestSequence = ++this.lastCarLocationAddressRequestSequence;
        getLastCarLocationAddress(carId)
            .then((geozone) => {
                if (
                    !this.mounted ||
                    requestSequence !== this.lastCarLocationAddressRequestSequence ||
                    !geozone?.address
                ) {
                    return;
                }

                const currentCarId = this.getSelectedCarId();
                const currentFirstBidPointIndex = this.getFirstBidPointIndex(this.state.bidPoints);
                const currentFirstBidPoint = this.state.bidPoints?.[currentFirstBidPointIndex];
                if (
                    currentCarId !== carId ||
                    currentFirstBidPointIndex < 0 ||
                    currentFirstBidPoint?.geozone?.address ||
                    !this.state.isEmptyMileageBid ||
                    !this.props.workRestInfo?.useLastCarLocationForEmptyMileageBid
                ) {
                    return;
                }

                const bidPoints = this.state.bidPoints.map((point, index) =>
                    index === currentFirstBidPointIndex ? { ...point, geozone, counterpartyPointId: null } : point,
                );
                this.setValue({ name: "bidPoints", value: bidPoints });
            })
            .catch(() => null);
    };

    loadData = (preparedFilter, callback) => {
        const params = {
            preparedFilter: { ...preparedFilter, status: bidStatusesEnum.Done },
            start: 0,
            length: 1,
            orderBy: ["firstBidPointPlanEnterDate desc"],
        };
        this.setState({ bookFieldsLoading: true });
        closeBidsList(params).then((res) => {
            if (res && res.data && res.data.length) {
                (this.bidPolicy.isCargoOwner()
                    ? getCargoBidForEdit(res.data[0].id)
                    : getTruckingBidForEdit(res.data[0].id)
                )
                    .then(callback)
                    .then(() => this.setState({ bookFieldsLoading: false }));
            } else {
                this.setState({ bookFieldsLoading: false });
            }
        });
    };

    loadDataByCoupling = (value, option) => {
        if (value && value.id) {
            const { driver, car, trailer, secondaryDriver } = option;
            this.setState(
                {
                    driver: { id: driver.id },
                    secondaryDriver: { id: secondaryDriver?.id },
                    carOption: { carId: car.id },
                    trailerOption: { trailerId: trailer?.trailerId },
                    bookFieldsLoading: false,
                },
                () => this.fillFirstEmptyMileagePointAddressFromCarLocation(),
            );
            this.updateCarAvailability(car.id);
            this.updateTrailerAvailability(trailer?.trailerId);
        } else {
            this.setState({
                driver: {},
                secondaryDriver: {},
                carOption: {},
                trailerOption: {},
                bookFieldsLoading: false,
            });
            this.updateCarAvailability();
            this.updateTrailerAvailability();
        }
    };

    loadDataByDriver = (value) => {
        const callback = (bid) => {
            if (bid.carOption) {
                this.setValue({ name: "carOption", value: bid.carOption });
                this.updateCarAvailability(bid.carOption.carId);
            }
            if (bid.trailerOption) {
                this.setValue({ name: "trailerOption", value: bid.trailerOption });
                this.updateTrailerAvailability(bid.trailerOption.trailerId);
            }
        };
        const { id } = value;
        this.loadData({ driverId: id }, callback);
    };

    loadDataByCar = (value) => {
        const callback = (bid) => {
            if (bid.driver) {
                this.setValue({ name: "driver", value: { user: null, id: bid.driver.id } });
            }
            if (bid.trailerOption) {
                this.setValue({ name: "trailerOption", value: bid.trailerOption });
            }
        };
        const { carId } = value;
        this.loadData({ carId }, callback);
    };

    loadDataByCargoOwner = (value) => {
        getDocuments(value).then((documents) => this.setValue({ name: "documents", value: documents }));
    };

    formatInitial = (bidRes, notClearDates = false) => {
        const { initialState, config } = this.props;
        const bidData = formatInitial(bidRes, initialState, this.props.action);
        bidData.expressBid = bidData.expressBid || { settings: {} };
        bidData.expenses ??= [];
        bidData.expenses?.forEach((x) => {
            x.resource = x.carId
                ? resourceEnum.CAR
                : x.driverId
                  ? resourceEnum.DRIVER
                  : x.trailerId
                    ? resourceEnum.TRAILER
                    : undefined;
        });
        const formattedBid = formatExtendedProperties(bidData, config.extendedProperties.config);
        const formattedCargos = bidData.cargos.map((c) =>
            formatExtendedProperties(c, config.cargos.config.extendedProperties.config),
        );
        const formattedPoints = bidData.bidPoints.map((p) =>
            formatExtendedProperties(p, config.bidPoints.config.extendedProperties.config),
        );
        if (this.props.isCopy && !notClearDates) {
            clearDates(formattedPoints);
        }
        return {
            ...formattedBid,
            cargos: formattedCargos,
            bidPoints: formattedPoints,
        };
    };

    getBidByDistributionBid = (distributionBidId, defState) => {
        this.setState({ isLoading: true, routeIsLoaded: false });
        this.pendingDistributionBidLockId = distributionBidId;
        this.setNeedRemoveInProcess(true);
        return setInProcessStatus({ id: distributionBidId, isInProcess: true }).then((res) => {
            this.pendingDistributionBidLockId = null;
            if (!res) {
                this.setNeedRemoveInProcess(false);
                if (!this.mounted) {
                    return false;
                }
                return getDistributionBid(distributionBidId).then((distributionBid) => {
                    this.showDistributionBidInProcessModal({ userName: distributionBid?.inProcessByUser?.shortName });
                    return false;
                });
            }

            this.lockedDistributionBidId = distributionBidId;
            if (!this.mounted) {
                return releaseInProcessStatus(distributionBidId).then((releaseResult) => {
                    if (releaseResult) {
                        this.clearDistributionBidLockState();
                        this.setNeedRemoveInProcess(false);
                    }
                    return false;
                });
            }

            this.setState({ lockedDistributionBidId: distributionBidId });
            return getForBid(distributionBidId)
                .then((bidRes) => {
                    this.setState(
                        {
                            ...defState,
                            ...this.formatInitial({
                                ...bidRes,
                                distributionBidId: parseInt(distributionBidId),
                                expenses: (bidRes.expenses ?? []).map((x) => ({ ...x, id: undefined })),
                            }),
                            distributionBidSource: "cargorun",
                        },
                        () => {
                            if (this.props.carId) {
                                this.loadDataByCar({ carId: parseInt(this.props.carId) });
                                this.updateCarAvailability(this.props.carId);
                            }
                        },
                    );
                    this.checkCounterpartyPoints(bidRes);
                })
                .catch((err) => {
                    return releaseInProcessStatus(distributionBidId).then((res) => {
                        if (res) {
                            this.clearDistributionBidLockState();
                            this.setNeedRemoveInProcess(false);
                        }
                        throw err;
                    });
                });
        });
    };

    initForm = (nextProps) => {
        const { match, action, tmpData, initialState, isPublic, roles, editRoute, confirmRoute, profile } =
            nextProps || this.props;
        const { isForwarder } = roles;
        this.initialRateValues = null;
        const defState = {
            isLoading: false,
            isValid: true,
            bidPointDatesChanged: false,
            plannedItemChangePoints: [],
            itemChangePointsToDelete: [],
            itemChangePointsChanged: false,
            ...(this.temperatureRegimeEnabled
                ? { temperatureRegime: { temperatureMinimum: "", temperatureMaximum: "" } }
                : {}),
            ...initialState,
            ...tmpData,
        };
        if (isForwarder) {
            defState.type = isPublic ? bidTypesEnum.Public : bidTypesEnum.Private;
        }
        if (action === "edit") {
            this.setState({ isLoading: true, routeIsLoaded: false });
            const getRouteByBidIdReq = this.bidPolicy.isCargoTruck() ? getRouteByBidId(match.params.id) : null;
            const getBidReq = this.bidPolicy.isCargoOwner()
                ? getCargoBidForEdit(match.params.id)
                : getTruckingBidForEdit(match.params.id);
            const getRouterOptions = this.bidPolicy.isCargoTruck() ? showDefaultRouteOptions() : null;
            return Promise.all([getBidReq, getRouteByBidIdReq, getRouterOptions]).then(
                ([bidRes, routeRes, defaultRouteOptions]) => {
                    this.initialRateValues = {
                        isVatTop: bidRes.isVatTop,
                        ndsTypeId: bidRes.ndsTypeId,
                        priceWithoutVatOnTop: bidRes.priceWithoutVatOnTop,
                    };
                    this.connection = new SignalRConnection({
                        url: "/signalr/bids",
                        connectionParams: match.params.id,
                        actions: [{ name: "Update", action: this.checkVersion }],
                        joinActionName: "Join",
                        leaveActionName: "Leave",
                    });
                    bidRes.bidPoints = withoutStartPoint(bidRes.bidPoints);
                    bidRes.expenses?.forEach((x) => {
                        x.resource = x.carId
                            ? resourceEnum.CAR
                            : x.driverId
                              ? resourceEnum.DRIVER
                              : x.trailerId
                                ? resourceEnum.TRAILER
                                : undefined;
                    });
                    const setStateData = (bidExtraData) => {
                        this.setState(
                            {
                                ...defState,
                                ...this.formatInitial(bidRes),
                                ...bidExtraData,
                                ...(bidRes.averageRateByDestination != null
                                    ? {
                                          averageRateByDestination: bidRes.averageRateByDestination,
                                          averageRateDate: bidRes?.bidPoints?.[0]?.planEnterDate,
                                      }
                                    : {}),
                                ...(bidRes.averageRate != null ? { bidAverageRate: bidRes.averageRate } : {}),
                                bidFinalPrice: bidRes.price ?? null,
                                isPreBibByInitial: bidRes.isPreBid,
                                initialCarId: bidRes.carOption.carId,
                                route: routeRes,
                                defaultRouteOptions,
                                routeIsLoaded: true,
                                editRoute,
                                confirmRoute,
                                pointsNotComparedToSegments: comparePointsWithSegments(
                                    bidRes.bidPoints || this.state.bidPoints,
                                    routeRes || this.state.route,
                                    true,
                                ),
                            },
                            () => {
                                if (bidRes.averageRate == null || bidRes.averageRateByDestination == null) {
                                    this.calculateAverageRate();
                                }
                            },
                        );

                        if (bidRes.carOption.carId) {
                            this.updateCarAvailability(bidRes.carOption.carId);
                        }
                        if (bidRes.trailerOption?.trailerId) {
                            this.updateTrailerAvailability(bidRes.trailerOption.trailerId);
                        }

                        this.checkCounterpartyPoints(bidRes);

                        this.setState({
                            cargoOwnerShareAccess: {
                                hasAccessToBids: bidRes.counterparty?.hasAccessToBids,
                                kpp: bidRes.counterparty?.kpp,
                                inn: bidRes.counterparty?.inn,
                                name: bidRes.counterparty?.name,
                                kppDisabled: !!bidRes.counterparty?.kpp,
                                contactPerson: bidRes.counterparty?.contactPerson,
                            },
                        });
                    };
                    const reqs = [
                        getItemChangePoints(bidRes.id).then((itemChangePoints) => ({ itemChangePoints })),
                        getBidItemsChanges(bidRes.id, true).then((itemChangesHistory) => ({ itemChangesHistory })),
                        getBidItem(bidRes.id).then((emergencyAreaPoints) => ({ emergencyAreaPoints })),
                        this.updatePlannedRefuelings(bidRes.id),
                    ];

                    if (profile.availableFeatureModules.includes(featureModuleTypesEnum.RouteControlPoints)) {
                        reqs.push(
                            getRouteControlBidPoints(bidRes.id).then((routeControlPoints) => ({ routeControlPoints })),
                        );
                    }

                    if (
                        profile.availableFeatureModules.includes(featureModuleTypesEnum.UnstableZones) &&
                        canManageOrganization(roles)
                    ) {
                        reqs.push(getUnstableZones(bidRes.id).then((unstableZones) => ({ unstableZones })));
                    }
                    if (profile.availableFeatureModules.includes(featureModuleTypesEnum.TripCoupling)) {
                        reqs.push(getCouplingByBid(bidRes).then((res) => ({ coupling: { id: res.id } })));
                    }
                    reqs.push(getItemIds(bidRes.id).then((itemIds) => ({ itemIds })));

                    Promise.all(reqs).then((responses) => {
                        setStateData(responses.reduce((acc, item) => ({ ...acc, ...item }), {}));
                    });
                },
            );
        } else if (this.props.distributionBidId) {
            return this.getBidByDistributionBid(this.props.distributionBidId, defState);
        } else if (this.props.matchingId) {
            this.setState({ isLoading: true, routeIsLoaded: false });
            return getForwardingOrder(this.props.matchingId).then((bidRes) => {
                if (!bidRes) {
                    return this.getBidByDistributionBid(this.props.cargorunId, defState);
                }
                bidRes.extendedProperties = [];
                bidRes.cargoOwnerDictionaryItemId = bidRes.counterpartyId;
                delete bidRes.id;
                delete bidRes.externalId;
                if (bidRes.carId) {
                    bidRes.carOption = { carId: bidRes.carId };
                }
                if (bidRes.driverId) {
                    bidRes.driver = { id: bidRes.driverId };
                }
                if (bidRes.trailerId) {
                    bidRes.trailerOption = { trailerId: bidRes.trailerId };
                }
                this.setState(
                    {
                        ...defState,
                        ...this.formatInitial({ ...bidRes, distributionBidId: parseInt(this.props.cargorunId) }),
                    },
                    () => {
                        if (bidRes.carId && !bidRes.driverId && !bidRes.trailerId) {
                            this.loadDataByCar({ carId: bidRes.carId });
                            this.updateCarAvailability(this.props.carId);
                        }
                    },
                );
                this.checkCounterpartyPoints(bidRes);
            });
        } else if (this.props.carId) {
            this.loadDataByCar({ carId: parseInt(this.props.carId) });
            this.updateCarAvailability(this.props.carId);
        }
        if (!this.props.config.legalPersonId.isHidden) {
            setTimeout(() => {
                getMainLegalPerson().then((res) =>
                    this.setValue({ name: "legalPersonId", value: res?.id, option: res }),
                );
            }, 0);
        }
        if (this.bidPolicy.canGetLogistTaskSettings()) {
            return getSettings().then((logistTaskSettings) =>
                this.setState({ ...defState, logistTaskSettings: logistTaskSettings ?? [] }),
            );
        } else {
            return this.setState({ ...defState });
        }
    };

    checkVersion = (data) => {
        if (
            !this.mounted ||
            data === null ||
            data.id !== this.state.id ||
            this.state.version === data.version ||
            this.state.versionIsOutdated
        ) {
            return;
        }

        this.setState({ versionIsOutdated: true });

        return this.props.addModal({
            name: "confirm",
            mod: "full",
            windowMod: "sm modal-window--centered",
            title: "Данные заявки устарели",
            text: "Обновите страницу, чтобы избежать ошибок при сохранении заявки",
            action: () => window.location.reload(),
            buttons: { confirm: "Обновить", cancel: "Отмена" },
        });
    };

    updatePlannedRefuelings = (bidId) => {
        if (this.bidPolicy.canViewPlanRefuelings({ refuelingSettings: this.props.refuelingSettings })) {
            return getPlannedRefuelings(bidId).then((data) => {
                const refueling = data || {};
                return { refueling };
            });
        } else {
            return Promise.resolve({ refueling: {} });
        }
    };

    checkContactPersons = (state) => {
        const { bidPoints } = state;
        bidPoints.forEach((x) => {
            const { contactPerson } = x;
            if (contactPerson) {
                if ((!contactPerson.phoneNumber || contactPerson.phoneNumber.length < 3) && !contactPerson.name) {
                    x.contactPerson = null;
                }
            }
        });
    };

    deleteBid = () => {
        const { id } = this.state;
        if (!id) {
            return {};
        }
        const { modal, mergeParams } = this.props;
        return modal({
            name: "confirm",
            title: "Удаление заявки",
            text: "Вы уверены, что хотите удалить заявку?",
            buttons: { confirm: "Удалить", cancel: "Отменить" },
            callback: () => this.props.history.push(paths.bid),
            action: () => deleteBid({ bidId: id }).then(() => mergeParams({})),
        });
    };

    onSubmitAndStart = () => {
        if (this.state.isFirstBid) {
            ymEvent("AfterRegistrationDraftRunToWorkClicked");
            this.setState({ isFirstBid: false });
        }
        return this.saveBid({ stayOpened: true }).then((data) => {
            const getBidReq = this.bidPolicy.isCargoOwner()
                ? getCargoBidForEdit(this.state.id)
                : getTruckingBidForEdit(this.state.id);
            return getBidReq.then((bidRes) => {
                const startBidAction = () => {
                    if (!!data && data.id) {
                        UmamiManager.track("ShipmentStarted");
                        return startBid(data.id).then((value) => {
                            return value && value.id
                                ? this.props.history.push(paths.bidShow("bid", data.id))
                                : this.setState({ isLoading: false });
                        });
                    } else {
                        this.setState({ isLoading: false });
                    }
                };
                const props = {
                    intFlags: this.props.organization.intFlags,
                    modal: this.props.modal,
                    workRestInfo: this.props.workRestInfo,
                    carOption: bidRes.carOption,
                    trailerOption: bidRes.trailerOption,
                    firstPoint: bidRes.bidPoints.filter(({ type }) => type !== bidPointTypesEnum.StartPoint)[0],
                    lastPoint: bidRes.bidPoints[bidRes.bidPoints.length - 1],
                    setLoading: (isLoading) => this.setState({ isLoading }),
                    action: startBidAction,
                    id: this.state.id,
                    isEmptyMileageBid: this.state.isEmptyMileageBid,
                    availableFeatureModules: this.props.profile.availableFeatureModules,
                };
                checkActiveBids(props);
            });
        });
    };

    saveBid = (options) => {
        const { stayOpened, checkCompounds } = options || {};
        const { isLoading, isVatTop, priceWithoutVatOnTop, cargoOwnerShareAccess, shareAccessEnabled, ...state } =
            this.state;
        if (isLoading) {
            return;
        }
        if (state.isEmptyMileageBid) {
            this.setDefaultEmptyBidData(state);
            this.setDefaultEmptyMileagePointLeaveDates(state);
        }
        if (this.isValidForm({ ...state, isVatTop, cargoOwnerShareAccess, shareAccessEnabled })) {
            const canApplyItemChangePoints = !state.isEmptyMileageBid && this.bidPolicy.canSeeBidItemChanges();
            const plannedItemChangePoints = canApplyItemChangePoints ? state.plannedItemChangePoints || [] : [];
            const changedItemChangePoints = canApplyItemChangePoints
                ? this.bidPolicy.isNewForm()
                    ? plannedItemChangePoints
                    : state.itemChangePointsChanged
                      ? state.itemChangePoints || []
                      : []
                : [];
            if (!changedItemChangePoints.every(isValidBidItemChangePoint)) {
                alert({
                    message: "Сохранение не удалось из-за ошибки, заполните обязательные поля в точках перецепки",
                    type: "warning",
                });
                return Promise.resolve(this.setState({ isValid: false }));
            }

            this.setState({ isLoading: true, isValid: true });
            this.checkContactPersons(state);
            delete state.plannedItemChangePoints;

            if (!this.props.profile.availableFeatureModules.includes(featureModuleTypesEnum.AccountsReceivable)) {
                delete state.payment;
            }

            delete state.route;
            delete state.routeIsLoaded;
            delete state.documentsExists;
            delete state.averageRateByDestination;
            delete state.averageRateDate;
            delete state.bidAverageRate;
            delete state.bidFinalPrice;
            delete state.routeDistanceMeters;
            if (this.props.action !== "new") {
                delete state.distributionBidId;
            }
            if (!state.isExpressBid) {
                delete state.expressBid;
            }
            delete state.itemChangePoints;
            delete state.itemChangePointsToDelete;
            delete state.itemChangePointsChanged;
            if (!this.temperatureRegimeEnabled) {
                delete state.temperatureRegime;
            }

            const formatted = serializeReq({
                ...state,
                isVatTop,
                secondaryDriverId: state.secondaryDriver?.id,
                price: isVatTop && priceWithoutVatOnTop ? priceWithoutVatOnTop : state.price,
            });
            const itemChangePointsToEdit = this.bidPolicy.isNewForm()
                ? plannedItemChangePoints
                : canApplyItemChangePoints && this.state.itemChangePointsChanged
                  ? this.state.itemChangePoints || []
                  : [];
            const bidId = formatted.id || this.state.id;
            const itemChangePointsToDelete =
                this.bidPolicy.isNewForm() || !canApplyItemChangePoints
                    ? []
                    : (this.state.itemChangePointsToDelete || []).map((item) => ({ ...item, bidId }));
            if (canApplyItemChangePoints && (itemChangePointsToEdit.length || itemChangePointsToDelete.length)) {
                formatted.itemChangePointsToEdit = {
                    ...(!this.bidPolicy.isNewForm() ? { bidId } : {}),
                    items: itemChangePointsToEdit.map(formatBidItemChangePoint),
                };
                formatted.itemChangePointsToDelete = itemChangePointsToDelete;
            }
            if (!formatted.logistTaskSettings) {
                formatted.logistTaskSettings = [];
            }
            formatted.bidPoints = withoutStartPoint(formatted.bidPoints);
            let pointsHasPolygon = false;
            formatted.expenses?.forEach((x) => {
                x.value = Number(x.value);
                x.date = dateTimeToView(x.date, "YYYY-MM-DD");
                delete x.files;
                delete x.resource;
            });
            formatted.bidPoints.forEach((x) => {
                if (!pointsHasPolygon && x.geozone.coordinates) {
                    pointsHasPolygon = true;
                }
                if (x.geozone.type === mapObjectTypesEnum.CounterpartyPoint) {
                    x.geozone.type = mapObjectTypesEnum.BidPoint;
                }
                x.planEnterDate = moment(x.planEnterDate).format("YYYY-MM-DDTHH:mm");
                if (x.planLeaveDate) {
                    x.planLeaveDate = moment(x.planLeaveDate).format("YYYY-MM-DDTHH:mm");
                }
                if (x.secondaryPlanEnterDate) {
                    x.secondaryPlanEnterDate = moment(x.secondaryPlanEnterDate).format("YYYY-MM-DDTHH:mm");
                }
                if (!x.scenarioId) {
                    x.scenarioId = 0;
                }
            });
            if (formatted.forwardingOrder?.fromPointPlanEnterTime) {
                formatted.forwardingOrder.fromPointPlanEnterTime = moment(
                    formatted.forwardingOrder.fromPointPlanEnterTime,
                ).format("YYYY-MM-DDTHH:mm");
            }
            if (formatted.clientBidDate) {
                formatted.clientBidDate = moment(formatted.clientBidDate).format("YYYY-MM-DD");
            }
            const { config } = this.props;
            formatted.extendedProperties = formatExtendedPropertiesForSave(formatted, config.extendedProperties.config);
            formatted.bidPoints.forEach((p) => {
                p.extendedProperties = formatExtendedPropertiesForSave(
                    p,
                    config.bidPoints.config.extendedProperties.config,
                );
            });
            formatted.cargos.forEach((c) => {
                c.extendedProperties = formatExtendedPropertiesForSave(
                    c,
                    config.cargos.config.extendedProperties.config,
                );
            });

            const alertParams = { successAlerts: !stayOpened, noContentAlerts: true };
            const bidAction = (extraParams = {}) => {
                const { matchingId, organization } = this.props;

                return (
                    shareAccessEnabled
                        ? giveAccessToBids({ ...(cargoOwnerShareAccess || {}), id: state.cargoOwnerDictionaryItemId })
                        : Promise.resolve()
                ).then(() => {
                    if (matchingId && this.state.distributionBidSource !== "cargorun") {
                        return applyBid({ ...formatted, ...extraParams }, alertParams);
                    }
                    if (this.bidPolicy.isCargoOwner()) {
                        return applyOpenBid({ ...formatted, ...extraParams }, alertParams);
                    } else {
                        if (this.bidPolicy.isNewForm()) {
                            UmamiManager.track("ShipmentDraftCreated");
                            if (pointsHasPolygon) {
                                ymEvent("CreateBidPointWithPolygon");
                            }
                        }
                        return applyCloseBid({ ...formatted, ...extraParams }, alertParams).then((response) => {
                            if (
                                response &&
                                !this.props.isOnboardingSuppressed &&
                                organization?.registrationStatus === organizationRegistrationStatusesEnum.Completed
                            ) {
                                return applyOnboarding({
                                    ...(this.props.onboardingOptions || {}),
                                    isCreateBidButtonFinished: true,
                                }).then(() => response);
                            }

                            return response;
                        });
                    }
                });
            };
            const hasParallelBid = this.state.itemChangePoints?.some((item) => item.parallelBidId);

            const action = () => {
                const { autoEnteredAt, enteredAt, enteredAtByLogist } = formatted.bidPoints[0];
                const firstBidPointEntered = !!(autoEnteredAt || enteredAt || enteredAtByLogist);
                const promises = [];
                if (hasParallelBid && this.props.action === "edit" && !firstBidPointEntered) {
                    promises.push(() =>
                        checkItemsBeforeParallelPoint({
                            bidId: formatted.id,
                            itemsToApply: {
                                carId: formatted.carOption.carId,
                                driverId: formatted.driver.id,
                                trailerId: formatted.trailerOption?.trailerId,
                                isPatch: false,
                            },
                        }).then(({ willBeChanged, parallelBidId, car, driver, trailer }) => {
                            return new Promise((resolve) => {
                                if (willBeChanged) {
                                    this.props.modal({
                                        name: "parallelBidChange",
                                        windowMod: "sm",
                                        title: parallelItemChangeModalTitle(car, driver, trailer),
                                        parallelBidId,
                                        withoutEscClose: true,
                                        actions: {
                                            update: () =>
                                                resolve({ parallelPointBehavior: parallelPointBehaviorsEnum.Update }),
                                            delete: () =>
                                                resolve({ parallelPointBehavior: parallelPointBehaviorsEnum.Delete }),
                                            cancel: () => resolve(false),
                                        },
                                    });
                                } else {
                                    resolve({});
                                }
                            });
                        }),
                    );
                }
                if (
                    this.props.action === "edit" &&
                    this.state.initialCarId !== formatted.carOption.carId &&
                    formatted.forwardingOrder
                ) {
                    promises.push(() =>
                        checkForwardingOrderCarHasChanged({
                            bidId: formatted.id,
                            carId: formatted.carOption.carId,
                        }).then(({ willBeChanged }) => {
                            return new Promise((resolve) => {
                                if (willBeChanged) {
                                    this.props.modal({
                                        name: "forwardingOrderCarChange",
                                        windowMod: "sm",
                                        title: "Обратите внимание",
                                        withoutEscClose: true,
                                        actions: {
                                            update: () =>
                                                resolve({
                                                    forwardingOrderCarChangeBehaviour:
                                                        forwardingOrderChangeBehavioursEnum.Update,
                                                }),
                                            delete: () =>
                                                resolve({
                                                    forwardingOrderCarChangeBehaviour:
                                                        forwardingOrderChangeBehavioursEnum.Delete,
                                                }),
                                            cancel: () => resolve(false),
                                        },
                                    });
                                } else {
                                    resolve({});
                                }
                            });
                        }),
                    );
                }
                if (this.props.action === "edit" && canApplyItemChangePoints && itemChangePointsToEdit.length) {
                    itemChangePointsToEdit.forEach((item) => {
                        promises.push(() =>
                            runBidItemChangePointChecks({
                                add: this.props.modal,
                                bid: { forwardingOrder: formatted.forwardingOrder },
                                bidId: formatted.id,
                                firstBidPointEntered,
                                hasParallelBidId: hasParallelBid,
                                item,
                            }),
                        );
                    });
                }
                if (promises.length > 0) {
                    const extraOptionsArr = [];
                    const promisesSeries = async () => {
                        for (let i = 0; i < promises.length; i++) {
                            const data = await promises[i]();
                            extraOptionsArr.push(data);
                        }
                    };
                    return promisesSeries().then(() => {
                        if (extraOptionsArr.some((item) => !item)) {
                            return false;
                        }
                        return bidAction(extraOptionsArr.reduce((acc, item) => ({ ...acc, ...item }), {}));
                    });
                } else {
                    return bidAction();
                }
            };
            const resolveAction = (res) => {
                if (res?.errorText) {
                    return this.handleSaveError(res.errorText);
                }
                if (res || res === 0) {
                    return this.successAction(res, stayOpened);
                } else {
                    return this.failureAction();
                }
            };
            if (checkCompounds && this.state.bidPointDatesChanged && !this.state.isCompoundBid) {
                const props = {
                    intFlags: this.props.organization.intFlags,
                    modal: this.props.modal,
                    carOption: this.state.carOption,
                    trailerOption: this.state.trailerOption,
                    firstPoint: this.state.bidPoints[0],
                    lastPoint: this.state.bidPoints[this.state.bidPoints.length - 1],
                    setLoading: (isLoading) => this.setState({ isLoading }),
                    id: this.state.id,
                    isEmptyMileageBid: this.state.isEmptyMileageBid,
                    action: () => action().then(resolveAction),
                };
                return checkCompoundBids(props);
            } else {
                return action().then(resolveAction);
            }
        } else {
            alert({ message: "Сохранение не удалось из-за ошибки, заполните обязательные поля", type: "warning" });
            logInvalidKeys(this.props.config, { ...state, isVatTop, cargoOwnerShareAccess, shareAccessEnabled });
        }
        return Promise.resolve(this.setState({ isValid: false }));
    };

    handleSaveError = (errorText) => {
        if (isDistributionBidInProcessError(errorText)) {
            this.setState({ isLoading: false });
            this.removeInProcessStatusForDistributionBid({ showModal: false });
            this.showDistributionBidInProcessModal({ message: errorText });
            return false;
        }

        alert({ type: "error", message: errorText, title: "Ошибка." });
        return this.failureAction();
    };

    successAction = ({ id }, stayOpened) => {
        this.clearDistributionBidLockState();
        this.setNeedRemoveInProcess(false);
        if (this.state.forwardingOrder) {
            ymEvent("ApplicationNeedActivated");
        }
        if (stayOpened) {
            this.setState({ id });
            return { id };
        } else {
            let path = this.props.action === "new" ? paths.bidEdit(id) : paths.bidShow("bid", id);
            const isSelfRegistered = !!this.props.organization?.registrationStatus;
            if (isSelfRegistered && this.props.action === "new") {
                path += (path.includes("?") ? "&" : "?") + "editRoute=true&confirmRoute=true";
            }
            this.props.history.push(path);
        }
    };

    onSubmit = (e) => {
        e && e.preventDefault();
        this.saveBid({
            checkCompounds: [bidStatusesEnum.Started, bidStatusesEnum.Planned].includes(this.state.status),
        });
    };

    setDefaultEmptyBidData = (state) => {
        const { nDSType, paymentType, cargoType } = this.props.enums;
        const { config } = this.props.config.cargos.config.extendedProperties;
        const objectVal = (o, arr) => (o ? o.value : arr && arr[0] ? arr[0].value : "");
        const { extendedProperties } = this.state.cargos[0];
        state.paymentTypeId = objectVal(
            paymentType.find((item) => item.label === emptyMileageBidDefaultValues.paymentType),
            paymentType,
        );
        state.price = emptyMileageBidDefaultValues.price;
        state.ndsTypeId = objectVal(
            nDSType.find((item) => item.label === emptyMileageBidDefaultValues.ndsType),
            nDSType,
        );
        state.cargoOwnerDictionaryItemId = emptyMileageBidDefaultValues.cargoOwnerDictionaryItemId;
        state.cargos = [
            {
                name: emptyMileageBidDefaultValues.cargoName,
                typeId: objectVal(
                    cargoType.find((item) => item.label === emptyMileageBidDefaultValues.cargoType),
                    cargoType,
                ),
                packType: emptyMileageBidDefaultValues.cargoPackType,
                extendedProperties,
            },
        ];
        Object.values(config).forEach((conf) => {
            if (!conf.required) {
                return;
            }
            state.cargos[0].extendedProperties[conf.inputProps.name] =
                conf.value || defaultExtendedPropValue(conf.typeCode);
        });
    };

    parseStayPeriodToSeconds = (value) => {
        if (typeof value === "number") {
            return Number.isFinite(value) && value > 0 ? value * 60 * 60 : null;
        }

        if (typeof value !== "string") {
            return null;
        }

        const match = value.trim().match(/^(\d+):([0-5]\d):([0-5]\d)$/);

        if (!match) {
            return null;
        }

        const [, hours, minutes, seconds] = match.map(Number);

        const totalSeconds = hours * 60 * 60 + minutes * 60 + seconds;

        return totalSeconds > 0 ? totalSeconds : null;
    };

    setDefaultEmptyMileagePointLeaveDates = (state) => {
        const planStayPeriodSeconds = this.parseStayPeriodToSeconds(
            this.props.workRestInfo?.emptyMileagePointStayPeriod,
        );

        if (!planStayPeriodSeconds) {
            return;
        }

        state.bidPoints = state.bidPoints.map((point) => {
            if (!point.planEnterDate) {
                return point;
            }

            return {
                ...point,
                planLeaveDate: moment(point.planEnterDate).add(planStayPeriodSeconds, "seconds").format(),
                planLeaveDateOffset: point.planLeaveDateOffset ?? null,
            };
        });
    };

    changeStatus = ({ value }) => {
        if (!value) {
            return null;
        }
        const translatedStatus = bidStatuses.find((item) => item.value === value).label;

        const { id } = this.state;
        const config = {
            name: "confirm",
            title: "Смена статуса",
            text: `Вы уверены, что хотите изменить статус заявки на "${translatedStatus}"?`,
            action: () => {
                this.setState({ statusIsLoading: true });
                setStatus(id, value)
                    .then((result) => {
                        const status = result ? { status: value } : {};
                        this.setState({ statusIsLoading: false, ...status });
                    })
                    .catch(() => this.setState({ statusIsLoading: false }));
            },
        };
        return this.props.modal(config);
    };

    renderStatusSelect = () => {
        const { config } = this.props;
        const { status } = config;
        return (
            <>
                <div className="form-group">
                    <strong>Заявка</strong>
                </div>
                {this.renderSelect({ ...status, onChange: this.changeStatus, fieldWrapper: "col-sm-8 col-md-5" })}
            </>
        );
    };

    renderPayment = () => {
        const { isEmptyMileageBid } = this.state;
        if (isEmptyMileageBid) {
            return null;
        }
        const { config, organization, profile } = this.props;
        const {
            invoiceTriggerType,
            paymentTypeId,
            price,
            hourlyPrice,
            ndsTypeId,
            isVatTop,
            priceWithoutVatOnTop,
            paymentPeriodType,
            paymentPeriodInDays,
            payment,
        } = config;
        const props = {
            invoiceTriggerType,
            paymentTypeId,
            price,
            hourlyPrice,
            ndsTypeId,
            isVatTop,
            paymentPeriodType,
            paymentPeriodInDays,
        };
        if (this.bidPolicy.canEditPayment()) {
            for (const key in props) {
                props[key] && (props[key].inputProps.disabled = "disabled");
            }
        } else {
            for (const key in props) {
                props[key] && delete props[key].inputProps.disabled;
            }
        }
        return (
            <Payment
                {...props}
                paymentConfig={payment.config}
                setValue={this.setValue}
                bidPolicy={this.bidPolicy}
                data={this.state}
                organization={organization}
                priceWithoutVatOnTop={priceWithoutVatOnTop}
                isVatTopValue={this.state.isVatTop}
                priceWithoutVatOnTopValue={this.state.priceWithoutVatOnTop}
                renderField={this.renderField}
                renderDateField={this.renderDateField}
                renderSelect={this.renderSelect}
                renderCheckbox={this.renderCheckbox}
                availableFeatureModules={profile.availableFeatureModules}
            />
        );
    };

    renderExpressBid = () => {
        const { isExpressBid } = this.props.config;
        const expressBid = this.state.expressBid || {};
        const onChange = ({ name, value }) => {
            const expressBidValue = { ...this.state.expressBid, settings: { ...expressBid.settings, [name]: value } };
            this.setValue({ name: "expressBid", value: expressBidValue });
        };
        return (
            <ExpressBid
                config={{ isExpressBid, ...this.props.config["expressBid.settings"].config }}
                isExpressBidValue={this.state.isExpressBid}
                renderCheckbox={this.renderCheckbox}
                renderField={this.renderField}
                onChange={onChange}
                expressBidSettings={expressBid.settings || {}}
            />
        );
    };

    renderBooksFields = () => {
        if (!this.bidPolicy.canSeeBookFields()) {
            return null;
        }
        const { modal, enums, profile, action, roles, refuelingSettings, organization } = this.props;
        const {
            coupling,
            driver,
            carOption,
            trailerOption,
            bookFieldsLoading,
            responsibleId,
            carStatus,
            trailerStatus,
            secondaryDriver,
            isEmptyMileageBid,
        } = this.state;
        const props = {
            coupling,
            profile,
            driver,
            secondaryDriver,
            carOption,
            trailerOption,
            modal,
            enums,
            action,
            bookFieldsLoading,
            responsibleId,
            bidPolicy: this.bidPolicy,
            firstPoint: this.state.bidPoints[0],
            roles,
            refuelingSettings,
            organization,
            isEmptyMileageBid
        };
        return (
            <BooksFields
                {...props}
                onChange={this.setValue}
                renderCheckbox={this.renderCheckbox}
                baseConfig={this.props.config}
                carStatus={carStatus}
                updateCarAvailability={this.updateCarAvailability}
                trailerStatus={trailerStatus}
                updateTrailerAvailability={this.updateTrailerAvailability}
            />
        );
    };

    renderExpenses = () => {
        const { expenses, driver, carOption, trailerOption, secondaryDriver, itemIds } = this.state;
        const { config, modal, dictionaries, enums } = this.props;
        const props = {
            items: expenses ?? [],
            onChange: this.setValue,
            entity: "expenses",
            config: config.expenses.config,
            Element: Expense,
            modal,
            enums,
            dictionaries,
            driver,
            secondaryDriver,
            carOption,
            trailerOption,
            itemIds,
        };
        return (
            <>
                <div className="form-group">
                    <strong>Расходы по заявке</strong>
                </div>
                <GroupElems {...props} />
                {this.bidPolicy.isOwner() && (
                    <div className="book-form__buttons">
                        <InlineBtn value="Добавить расходы" cname="plus" action={this.addExpense} />
                    </div>
                )}
            </>
        );
    };

    renderBidTypeOptions = () => {
        const { entityOptions, enums, dictionaries, modal, roles, canEditBooks } = this.props;
        const props = {
            entityOptions,
            enums,
            dictionaries,
            setValue: this.setValue,
            entityType: entityTypesEnum.TruckingBid,
            typeOptionsValue: this.state.typeOptions || [],
            isEmptyMileageBid: this.state.isEmptyMileageBid,
            showAllInEmptyMileageBid: this.customFieldsEnabled,
            modal,
            roles,
            canEditBooks,
        };
        return <TypeOptions {...props} />;
    };

    renderDriverPayment = () => {
        if (!this.bidPolicy.isCargoTruck()) {
            return null;
        }
        const { driverHourlyPrice, driverNdsTypeId, driverPaymentTypeId, driverPrice } = this.props.config;
        const props = { driverHourlyPrice, driverNdsTypeId, driverPaymentTypeId, driverPrice };
        return <DriverPayment {...props} renderField={this.renderField} renderSelect={this.renderSelect} />;
    };

    renderCargoActions = () => {
        const actions = [
            {
                value: "Добавить груз",
                cname: "plus",
                action: this.addCargo,
            },
        ];
        return actions.map((item, i) => <InlineBtn key={i} {...item} />);
    };

    renderLogistTasks = () => {
        if (!this.bidPolicy.canSeeLogistTaskSettings()) {
            return null;
        }
        const { logistTaskSettings } = this.state;
        const props = {
            logistTaskSettings: logistTaskSettings || [],
            onChange: this.setValue,
            config: this.props.config.logistTaskSettings.config,
            renderCheckbox: this.renderCheckbox,
            renderSelect: this.renderSelect,
            roles: this.props.roles,
        };
        return <LogistTaskSettings {...props} />;
    };

    renderPlannedItemChangePoints = () => {
        if (!this.bidPolicy.canSeeBidItemChanges() || this.state.isEmptyMileageBid) {
            return null;
        }

        const isNewForm = this.bidPolicy.isNewForm();
        const bidPoints = withoutStartPoint(this.state.bidPoints || []);
        const firstBidPoint = bidPoints[0] || {};
        const { autoEnteredAt, enteredAt, enteredAtByLogist } = firstBidPoint;
        const firstBidPointEntered = !!(autoEnteredAt || enteredAt || enteredAtByLogist);
        const bid = {
            ...this.state,
            bidPoints,
            car: this.state.carOption,
            estimations: this.state.estimations || [],
            forwardingOrder: this.state.forwardingOrder,
        };
        const hasParallelBidId = (this.state.itemChangePoints || []).some((item) => item.parallelBidId);
        return (
            <PlannedItemChangePoints
                addModal={this.props.addModal || this.props.modal}
                bid={bid}
                bidId={this.state.id}
                bidLastCarLocation={this.state.bidLastCarLocation}
                firstBidPointEntered={firstBidPointEntered}
                hasParallelBidId={hasParallelBidId}
                itemChangesHistory={this.state.itemChangesHistory}
                items={isNewForm ? this.state.plannedItemChangePoints || [] : this.state.itemChangePoints || []}
                itemsName={isNewForm ? "plannedItemChangePoints" : "itemChangePoints"}
                deletedItems={this.state.itemChangePointsToDelete || []}
                deletedItemsName="itemChangePointsToDelete"
                onChange={this.setItemChangePointsValue}
                organization={this.props.organization}
                route={this.state.route}
                switchMap={this.props.switchMap}
            />
        );
    };

    renderCargos = () => {
        const { cargos, isEmptyMileageBid } = this.state;
        if (isEmptyMileageBid && !this.hasEmptyMileageCargoFields()) {
            return null;
        }
        const { config, entityOptions, enums, dictionaries, modal, roles, canEditBooks } = this.props;
        const { cargoOwnerDictionaryItemId, partnerId } = config;
        const propsConfig = {
            ...this.props.config.cargos.config,
            cargoOwnerDictionaryItemId,
            partnerId,
        };
        const props = {
            items: cargos,
            onChange: this.setValue,
            entity: "cargos",
            config: propsConfig,
            Element: Cargo,
            enums,
            dictionaries,
            entityOptions,
            isEmptyMileageBid,
            customFieldsEnabled: this.customFieldsEnabled,
            hideElement: isEmptyMileageBid,
            modal,
            roles,
            canEditBooks,
            canUseMultipleBidCargos: this.bidPolicy.canUseMultipleBidCargos(),
        };
        return (
            <>
                {!isEmptyMileageBid && this.bidPolicy.canUseMultipleBidCargos() && (
                    <div className="form-group">
                        <strong>Данные о грузе</strong>
                    </div>
                )}
                <GroupElems {...props} />
                {!isEmptyMileageBid && this.bidPolicy.canUseMultipleBidCargos() && this.bidPolicy.isOwner() && (
                    <div className="book-form__buttons">{this.renderCargoActions()}</div>
                )}
            </>
        );
    };

    hasEmptyMileageCatalogOptions = (entityType) =>
        this.props.entityOptions.some((option) => {
            const intFlags = option.intFlags;
            const isHidden = (intFlags & catalogOptionHiddenFlagValue) === catalogOptionHiddenFlagValue;
            const isShownInEmptyMileageBid =
                option.showInEmptyMileageBids ||
                (catalogOptionShowInEmptyMileageBidsFlagValue > 0 &&
                    (intFlags & catalogOptionShowInEmptyMileageBidsFlagValue) ===
                        catalogOptionShowInEmptyMileageBidsFlagValue);
            return (
                option.entityType === entityType &&
                !!option.catalogPropertyName &&
                !isHidden &&
                (this.customFieldsEnabled || isShownInEmptyMileageBid)
            );
        });

    hasEmptyMileageCargoFields = () => {
        const cargoExtendedPropertiesConfig = this.getEmptyMileageExtendedPropertiesConfig(
            this.props.config.cargos.config.extendedProperties,
        );
        return (
            Object.keys(cargoExtendedPropertiesConfig).length > 0 ||
            this.hasEmptyMileageCatalogOptions(entityTypesEnum.Cargo)
        );
    };

    renderPoints = () => {
        const {
            id,
            bidPoints,
            isEmptyMileageBid,
            carStatus,
            status,
            cargoOwnerDictionaryItemId,
            counterpartiesData,
            couplingDates,
            forwardingOrder,
            averageRateByDestination,
            averageRateDate,
            bidAverageRate,
        } = this.state;
        const { switchMap, roles, enums, modal, profile, entityOptions, dictionaries, canEditBooks, organization } =
            this.props;
        const bidPointsFormatted = bidPoints.reduce((acc, curr) => {
            const { type } = curr;
            if (type === bidPointTypesEnum.StartPoint) {
                return acc;
            }
            acc.push({ ...curr, pointIndex: this.bidPointIndex });
            if (type !== bidPointTypesEnum.ServicePoint) {
                this.bidPointIndex++;
            }
            return acc;
        }, []);

        const config = this.props.config.bidPoints.config;
        const props = {
            bidId: id,
            switchMap,
            roles,
            config,
            enums,
            modal,
            organization,
            carStatus,
            isEmptyMileageBid,
            customFieldsEnabled: this.customFieldsEnabled,
            counterpartiesData,
            updateCounterpartiesData: this.updateCounterpartiesData,
            availableFeatureModules: profile.availableFeatureModules,
            bidPolicy: this.bidPolicy,
            bidCounterpartyId: cargoOwnerDictionaryItemId,
            inFinishedStatus: this.bidPolicy.inFinishedStatus(),
            entityOptions,
            dictionaries,
            canEditBooks,
            couplingDates,
            profile,
            forwardingOrder,
            setForwardingData: (newData) => this.setValue({ name: "forwardingOrder", value: newData }),
            forwardingOrderConfig: this.props.config.forwardingOrder.config,
            firstLoadPointIndex: bidPoints.findIndex((item) =>
                [bidPointTypesEnum.LoadPoint, bidPointTypesEnum.UnloadPoint].includes(item.type),
            ),
            workRestInfo: this.props.workRestInfo,
            averageRateByDestination,
            averageRateDate,
            bidAverageRate,
        };

        return (
            <>
                <GroupElems
                    {...props}
                    items={bidPointsFormatted}
                    onChange={this.setValue}
                    entity="bidPoints"
                    Element={Point}
                    bidStatus={status}
                    onPointRemoved={() => {
                        this.setState({
                            pointsNotComparedToSegments: comparePointsWithSegments(
                                this.state.bidPoints,
                                this.state.route,
                                true,
                            ),
                        });
                    }}
                    onPointDatesChange={() => this.setState({ bidPointDatesChanged: true })}
                />
                {this.bidPolicy.isOwner() && !this.bidPolicy.inFinishedStatus() && (
                    <div className="book-form__buttons">{this.renderPointActions()}</div>
                )}
            </>
        );
    };

    addCargo = () => {
        const { cargos } = this.state;
        this.setState({ cargos: [...cargos, cargo] });
    };

    addExpense = () => {
        const { expenses } = this.state;
        this.setState({ expenses: [...(expenses ?? []), {}] });
    };

    getBidInfo = (id) => {
        const req = this.bidPolicy.isCargoOwner() ? getCargoBidForEdit : getTruckingBidForEdit;
        return req(id).then((bidRes) => {
            bidRes.bidPoints = withoutStartPoint(bidRes.bidPoints);
            if (this.bidPolicy.isCargoTruck()) {
                return getRouteByBidId(id).then((routeRes) => {
                    this.setState({
                        ...this.formatInitial(bidRes, true),
                        route: routeRes,
                        routeIsLoaded: true,
                        isLoading: false,
                    });
                    return [bidRes, routeRes];
                });
            } else {
                this.setState({ ...this.formatInitial(bidRes, true), isLoading: false });
                return [bidRes];
            }
        });
    };

    reloadRoute = () => {
        const { match } = this.props;
        const { id } = this.state;
        this.setState({ routeIsLoaded: false });
        const getRouteByBidIdReq = this.bidPolicy.isCargoTruck() ? getRouteByBidId(match.params.id || id) : [];
        getRouteByBidIdReq.then((route) => this.setState({ route, routeIsLoaded: true }));
    };

    renderRoute = () => {
        if (this.bidPolicy.isNewForm()) {
            return null;
        }
        if (!this.state.routeIsLoaded || !this.state.refueling || !this.state.itemChangePoints) {
            return <Loader />;
        }
        if (!this.bidPolicy.canSeeRoute()) {
            return null;
        }
        const {
            cargos,
            bidPoints,
            route,
            defaultRouteOptions,
            id,
            editRoute,
            confirmRoute,
            carOption,
            refueling,
            pointsNotComparedToSegments,
            itemChangePoints,
            emergencyAreaPoints,
            routeControlPoints,
            unstableZones,
        } = this.state;
        const { modal, tileUrls, history, profile, refuelingServiceType, organization, refuelingSettings, roles } =
            this.props;

        const props = {
            pointsNotComparedToSegments,
            cargos,
            refueling,
            bidPoints,
            route,
            defaultRouteOptions,
            modal,
            tileUrls,
            getBidInfo: this.getBidInfo,
            history,
            submitBid: () => this.saveBid({ stayOpened: true }),
            onSubmitAndStart: this.onSubmitAndStart,
            setValue: this.setValue,
            onCloseAction: this.reloadRoute,
            availableFeatureModules: profile.availableFeatureModules,
            bidId: id,
            carOption,
            editRoute,
            confirmRoute,
            fromShowPage: this.props.confirmRoute,
            itemChangePoints,
            emergencyAreaPoints,
            routeControlPoints,
            unstableZones,
            refuelingServiceType,
            organization,
            refuelingSettings,
            roles,
            isFirstBid: this.state.isFirstBid,
        };
        return <Route {...props} />;
    };

    runToWork = () => {
        this.saveBid({ stayOpened: true })
            .then(({ id }) => this.getBidInfo(id))
            .then(() => this.setState({ editRoute: true, confirmRoute: true, isLoading: false }));
    };

    renderActions = () => {
        const { profile, organization } = this.props;
        const userPolicy = policy("user", { user: { profile } });

        const isSelfRegistered = !!organization?.registrationStatus;

        const value = this.bidPolicy.isNewForm()
            ? isSelfRegistered
                ? "Проверить маршрут и запустить в работу"
                : userPolicy.hasPlanningModule() && this.bidPolicy.isCargoTruck()
                  ? "Сохранить как черновик"
                  : "Создать заявку"
            : "Обновить заявку";
        return (
            <>
                <input type="submit" className="btn btn-brand mr-2 btn-sm" value={value} />
                {this.bidPolicy.canRunToWork({ userPolicy, usesBidsApproval: organization.usesBidsApproval }) && (
                    <a className="btn btn-brand btm mr-2 btn-sm" onClick={this.runToWork}>
                        Запустить в работу
                    </a>
                )}
                {this.bidPolicy.canSeeEditRouteBtn() && (
                    <a
                        className="btn btn-secondary mr-2 btn-sm"
                        onClick={() => this.setState({ editRoute: true, confirmRoute: false })}
                    >
                        Изменить маршрут
                    </a>
                )}
                {this.bidPolicy.canDelete() && (
                    <a className="btn btn-secondary btn-sm float-right" onClick={this.deleteBid}>
                        Удалить
                    </a>
                )}
            </>
        );
    };

    renderHead = () => {
        return (
            <div className="book-form__head">
                <div className="book-form__title">
                    {this.props.title}
                    {this.bidPolicy.canSeeEditRouteBtn() && (
                        <a
                            className="book-form__head--btn btn btn-secondary float-right btn-sm d-none d-md-block d-lg-block"
                            onClick={() => this.setState({ editRoute: true, confirmRoute: false })}
                        >
                            Изменить маршрут
                        </a>
                    )}
                </div>
                <div className="book-form__close close close--sm" onClick={this.close} />
            </div>
        );
    };

    renderMainData = () => {
        const props = {
            renderField: this.renderField,
            renderDateField: this.renderDateField,
            renderSelect: this.renderSelect,
            renderCheckbox: this.renderCheckbox,
            renderFlags: this.renderFlags,
            config: this.props.config,
            bidPolicy: this.bidPolicy,
            setValue: this.setValue,
            documentsExists: this.state.documentsExists,
            documents: this.state.documents,
            isEmptyMileageBid: this.state.isEmptyMileageBid,
            temperatureRegimeEnabled: this.temperatureRegimeEnabled,
            temperatureRegime: this.state.temperatureRegime,
            counterpartyInsolvencyRegistryEntry:
                this.state.counterpartiesData[this.state.cargoOwnerDictionaryItemId]?.insolvencyRegistryEntry,
            counterpartyComment: this.state.counterpartiesData[this.state.cargoOwnerDictionaryItemId]?.comment,
            cargoOwnerDictionaryItemId: this.state.cargoOwnerDictionaryItemId,
            availableFeatureModules: this.props.profile.availableFeatureModules,
            values: this.state,
            trackEmptyMileageBidTypesEnabled: this.trackEmptyMileageBidTypesEnabled,
            isNewForm: this.bidPolicy.isNewForm(),
            modal: this.props.modal,
            roles: this.props.roles,
            profile: this.props.profile,
            organization: this.props.organization,
        };
        return <MainData {...props} />;
    };

    renderBody = () => {
        this.bidPointIndex = 0;
        const { statusIsLoading, isEmptyMileageBid } = this.state;
        if (statusIsLoading) {
            return <Loader />;
        }
        return (
            <>
                <ProvideTabIndex value={{ baseTabIndex: 100 }}>
                    {this.bidPolicy.canChangeStatus() && this.renderStatusSelect()}
                    {this.renderMainData()}
                    <div className="d-flex flex-column">
                        {this.renderExtendedProperties()}
                        {this.renderBidTypeOptions()}
                    </div>
                </ProvideTabIndex>
                <ProvideTabIndex value={{ baseTabIndex: 200 }}>
                    {this.renderExpressBid()}
                    <HiddenDataWrapper>{this.renderPayment()}</HiddenDataWrapper>
                    {this.renderBooksFields()}
                </ProvideTabIndex>
                <ProvideTabIndex value={{ baseTabIndex: 300 }}>
                    {!isEmptyMileageBid && this.renderExpenses()}
                </ProvideTabIndex>
                <ProvideTabIndex value={{ baseTabIndex: 400 }}>{this.renderCargos()}</ProvideTabIndex>
                <ProvideTabIndex value={{ baseTabIndex: 500 }}>{this.renderPoints()}</ProvideTabIndex>
                <ProvideTabIndex value={{ baseTabIndex: 600 }}>
                    {this.renderPlannedItemChangePoints()}
                    {!isEmptyMileageBid && this.renderLogistTasks()}
                </ProvideTabIndex>
                {!this.bidPolicy.inFinishedStatus() && this.renderRoute()}
            </>
        );
    };
}
